import { canv } from "@/canv/global";
import { getFirstSelected } from "@/canvasController/helpers";
import { ArrowWidget } from "@/elements/arrow";
import type { Group } from "@/group";
import type { ArrowPlace } from "@/group/types";
import { selectionManager } from "./SelectionManager";
import { transformManager } from "./TransformManager";
import { createDuplicateGroup } from "./utils";

class FastActionManager {
    createArrowAndResize() {
        // тут вы вызываем turnMode и setSelected, каждый из которых сбрасывает this.fastActionArrow, this.fastActionGroup, ...
        // поэтому тут важно установить this.fastActionArrow, this.fastActionGroup после setSelected и turnMode

        const previousSelected = getFirstSelected();

        // Готовим arrow
        const fastActionArrow = canv.toolBox.TryGetToolByName("arrowTool").getWidgetWrapper();
        canv.canvasController.addGroup(fastActionArrow);

        // переключаемся на resize
        selectionManager.setSelected([fastActionArrow]);
        canv.toolBox.TryGetToolByName("cursorTool").turnMode("resize");
        canv.widgetMenu.hide();
        transformManager.setCatchPointer("lastPoint");

        this.fastActionGroup = createDuplicateGroup(previousSelected) as Group;
        this.fastActionArrow = fastActionArrow as Group;

        const pt = canv.actionManager.getActionPoint();
        const onlyChild = fastActionArrow.onlyChild() as unknown as ArrowWidget;

        if (onlyChild) {
            onlyChild.getLastPoint().x = pt.x;
            onlyChild.getLastPoint().y = pt.y;
            onlyChild.firstPointPlace = this.fastActionSide; // this.fastActionSide не сбрасывается в softResetMode
            onlyChild.lastPointPlace = this.setupFastActionGroup();
        }

        canv.eventManager.openRecord();

        // создаем group
        canv.eventManager.createBuildEvent("create", fastActionArrow);

        // привязываем arrow
        previousSelected.TryAddArrow(
            onlyChild,
            this.fastActionSide as ArrowPlace,
            "firstPoint",
            0.5,
        );
        previousSelected.updateDims(); // чтобы подтянуть стрелку?

        canv.eventManager.closeRecord();
    }

    setupFastActionGroup(): ArrowPlace {
        let shadowSide: ArrowPlace = "left";

        if (this.fastActionSide === "right") {
            shadowSide = "left";
        } else if (this.fastActionSide === "left") {
            shadowSide = "right";
        } else if (this.fastActionSide === "bottom") {
            shadowSide = "top";
        } else {
            shadowSide = "bottom";
        }

        let moveX = 0;
        let moveY = 0;

        const onlyChild = this.fastActionArrow?.onlyChild() as unknown as ArrowWidget;

        if (!onlyChild || !this.fastActionGroup) {
            return shadowSide;
        }

        if (shadowSide === "left") {
            moveX = onlyChild.getLastPoint().x - this.fastActionGroup.startX;
            moveY =
                onlyChild.getLastPoint().y -
                this.fastActionGroup.startY -
                this.fastActionGroup.height / 2;
        } else if (shadowSide === "right") {
            moveX =
                onlyChild.getLastPoint().x -
                this.fastActionGroup.startX -
                this.fastActionGroup.width;
            moveY =
                onlyChild.getLastPoint().y -
                this.fastActionGroup.startY -
                this.fastActionGroup.height / 2;
        } else if (shadowSide === "top") {
            moveX =
                onlyChild.getLastPoint().x -
                this.fastActionGroup.startX -
                this.fastActionGroup.width / 2;
            moveY = onlyChild.getLastPoint().y - this.fastActionGroup.startY;
        } else if (shadowSide === "bottom") {
            moveX =
                onlyChild.getLastPoint().x -
                this.fastActionGroup.startX -
                this.fastActionGroup.width / 2;
            moveY =
                onlyChild.getLastPoint().y -
                this.fastActionGroup.startY -
                this.fastActionGroup.height;
        }

        this.fastActionGroup.translateGroup(moveX, moveY);

        return shadowSide;
    }

    findGroupByFastActionArrow() {
        let fastActionArrowMagneted: Group | null = null;

        for (let i = canv.canvasController.items.length - 1; i >= 0; i--) {
            // @ts-expect-error вот тут .TryMagnet(this, "lastPoint"); this - cursorTool, что не правильно. Просто внутри этот параметр используется только в TryRemoveArrow, где мы и не ожидаем удаление стрелок
            const res = canv.canvasController.items[i].TryMagnet(this, "lastPoint");
            const isTheSameArrow = canv.canvasController.items[i].arrows.some(
                (arrow) => arrow.arrowItemUID === this.fastActionArrow?.children[0].UID,
            );

            if (isTheSameArrow) {
                continue;
            }

            if (res.status) {
                fastActionArrowMagneted = canv.canvasController.items[i];
                break;
            }
        }

        // TryMagnet без mag включает highlight, поэтому тут его выключаем
        // note: подсветка которая все таки есть получается в результате TryMagnet из arrow widget
        for (let i = canv.canvasController.items.length - 1; i >= 0; i--) {
            canv.canvasController.items[i].TurnOffMagnetViewRecursively();
        }

        return fastActionArrowMagneted;
    }

    emplaceFastActionGroup(foundGroup: Group | null = null) {
        // setSelectedAndInteraction сбрасывает this.fastActionArrow, this.fastActionGroup, поэтому он в конце
        canv.eventManager.openRecord();

        const side = this.setupFastActionGroup();

        // это нужно, так как после оконачания resize происходи resizingStop, который делает lastPointPlace=""
        this.fastActionArrow?.fixWidgets("resize");
        const onlyChild = this.fastActionArrow?.onlyChild() as unknown as ArrowWidget;

        if (onlyChild) {
            onlyChild.lastPointPlace = side;
        }

        this.fastActionArrow?.saveWidgets("resize");

        if (!foundGroup) {
            // создаем group если мы не нашли группу в которую дотянули линию
            canv.canvasController.addGroup(this.fastActionGroup as Group);
            canv.eventManager.createBuildEvent("create", this.fastActionGroup);
        }

        // добавляем arrow
        if (this.fastActionArrow?.onlyChild() instanceof ArrowWidget) {
            this.fastActionGroup?.TryAddArrow(
                this.fastActionArrow?.onlyChild() as unknown as ArrowWidget,
                side,
                "lastPoint",
                0.5,
            );
        }
        this.fastActionGroup?.updateDims();

        if (foundGroup) {
            // удаляем из грида фантомную группу, если мы нашли элемен куда дотянули линию
            this.fastActionGroup?.beforeDeletion();
        }

        canv.eventManager.closeRecord();

        canv.toolBox
            .TryGetToolByName("cursorTool")
            .setSelectedAndInteraction((foundGroup || this.fastActionGroup) as Group);
    }

    drawFastAction() {
        if (
            canv.canvasController.selected.length !== 1 ||
            !canv.canvasController.selected[0].isItemWrapper() ||
            canv.canvasController.selected[0].onlyChild()?.fastAction === ""
        ) {
            return;
        }

        const selectedProps = canv.canvasController.getSelectedProps();
        let bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
        let bubblePadding = canv.actionManager.getConstant(this.bubblePadding);
        const hoverMulti = 1.5;

        canv.ctx.beginPath(); // нужен, так как инчае fill заполняет предыдущие path, например catchPointers
        canv.ctx.fillStyle = "#0B99FF"; // #0B99FF
        canv.ctx.strokeStyle = "white";

        // RIGHT SIDE //////////////////////////////////////////////////////////////////////
        if (this.fastActionSide === "right") {
            bubblePadding = bubblePadding - (hoverMulti - 1) * bubbleRadius; // чтобы центр bubble остался на месте
            bubbleRadius = hoverMulti * bubbleRadius;
        }
        canv.ctx.lineWidth = bubbleRadius / 2;
        canv.ctx.beginPath();
        canv.ctx.arc(
            selectedProps.startX + selectedProps.width + bubblePadding + bubbleRadius,
            selectedProps.startY + selectedProps.height / 2,
            bubbleRadius,
            0,
            2 * Math.PI,
            false,
        );
        canv.ctx.stroke();
        canv.ctx.fill();
        if (this.fastActionSide === "right") {
            bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
            bubblePadding = canv.actionManager.getConstant(this.bubblePadding);
        }
        ////////////////////////////////////////////////////////////////////////////////////

        // BOTTOM SIDE /////////////////////////////////////////////////////////////////////
        if (this.fastActionSide === "bottom") {
            bubblePadding = bubblePadding - (hoverMulti - 1) * bubbleRadius; // чтобы центр bubble остался на месте
            bubbleRadius = hoverMulti * bubbleRadius;
        }
        canv.ctx.lineWidth = bubbleRadius / 2;
        canv.ctx.beginPath();
        canv.ctx.arc(
            selectedProps.startX + selectedProps.width / 2,
            selectedProps.startY + selectedProps.height + bubblePadding + bubbleRadius,
            bubbleRadius,
            0,
            2 * Math.PI,
            false,
        );
        canv.ctx.stroke();
        canv.ctx.fill();
        if (this.fastActionSide === "bottom") {
            bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
            bubblePadding = canv.actionManager.getConstant(this.bubblePadding);
        }
        ////////////////////////////////////////////////////////////////////////////////////

        // LEFT SIDE /////////////////////////////////////////////////////////////////////
        if (this.fastActionSide === "left") {
            bubblePadding = bubblePadding - (hoverMulti - 1) * bubbleRadius; // чтобы центр bubble остался на месте
            bubbleRadius = hoverMulti * bubbleRadius;
        }
        canv.ctx.lineWidth = bubbleRadius / 2;
        canv.ctx.beginPath();
        canv.ctx.arc(
            selectedProps.startX - bubblePadding - bubbleRadius,
            selectedProps.startY + selectedProps.height / 2,
            bubbleRadius,
            0,
            2 * Math.PI,
            false,
        );
        canv.ctx.stroke();
        canv.ctx.fill();
        if (this.fastActionSide === "left") {
            bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
            bubblePadding = canv.actionManager.getConstant(this.bubblePadding);
        }

        // TOP SIDE /////////////////////////////////////////////////////////////////////
        if (this.fastActionSide === "top") {
            bubbleRadius = hoverMulti * bubbleRadius;
        }

        bubblePadding = canv.actionManager.getZoomConstant(this.topBubbleMove) / 2 - bubbleRadius;
        canv.ctx.lineWidth = bubbleRadius / 2;
        canv.ctx.beginPath();
        canv.ctx.arc(
            selectedProps.startX + selectedProps.width / 2,
            selectedProps.startY - bubblePadding - bubbleRadius,
            bubbleRadius,
            0,
            2 * Math.PI,
            false,
        );
        canv.ctx.stroke();
        canv.ctx.fill();

        if (this.fastActionSide === "top") {
            bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
            bubblePadding = canv.actionManager.getConstant(this.bubblePadding);
        }
    }

    drawFastActionShadow() {
        if (this.fastActionGroup == null) {
            return;
        }

        if (!this.findGroupByFastActionArrow()) {
            this.setupFastActionGroup();
            canv.ctx.globalAlpha = 0.3;
            this.fastActionGroup.draw();
            canv.ctx.globalAlpha = 1;
        }
    }

    tryFastAction() {
        if (
            canv.canvasController.selected.length !== 1 ||
            !canv.canvasController.selected[0].isItemWrapper() ||
            canv.canvasController.selected[0].onlyChild()?.fastAction === ""
        ) {
            return;
        }

        if (canv.toolBox.TryGetToolByName("cursorTool").isNothingWhenInteraction().status)
            return false;

        const selectedProps = canv.canvasController.getSelectedProps();
        const bubbleRadius = canv.actionManager.getZoomConstant(this.bubbleRadius);
        let bubblePadding = canv.actionManager.getConstant(this.bubblePadding);

        const pt = canv.actionManager.getActionPoint();

        if (
            pt.x > selectedProps.startX + selectedProps.width + bubblePadding &&
            pt.x < selectedProps.startX + selectedProps.width + bubblePadding + 2 * bubbleRadius
        ) {
            if (
                pt.y > selectedProps.startY + selectedProps.height / 2 - bubbleRadius &&
                pt.y < selectedProps.startY + selectedProps.height / 2 + bubbleRadius
            ) {
                this.fastActionSide = "right";
                return true;
            }
        }

        if (
            pt.x > selectedProps.startX + selectedProps.width / 2 - bubbleRadius &&
            pt.x < selectedProps.startX + selectedProps.width / 2 + bubbleRadius
        ) {
            if (
                pt.y > selectedProps.startY + selectedProps.height + bubblePadding &&
                pt.y <
                    selectedProps.startY + selectedProps.height + bubblePadding + 2 * bubbleRadius
            ) {
                this.fastActionSide = "bottom";
                return true;
            }
        }

        if (
            pt.x < selectedProps.startX - bubblePadding &&
            pt.x > selectedProps.startX - bubblePadding - 2 * bubbleRadius
        ) {
            if (
                pt.y > selectedProps.startY + selectedProps.height / 2 - bubbleRadius &&
                pt.y < selectedProps.startY + selectedProps.height / 2 + bubbleRadius
            ) {
                this.fastActionSide = "left";
                return true;
            }
        }

        bubblePadding = canv.actionManager.getZoomConstant(this.topBubbleMove) / 2 - bubbleRadius; // специальный padding для top
        if (
            pt.x > selectedProps.startX + selectedProps.width / 2 - bubbleRadius &&
            pt.x < selectedProps.startX + selectedProps.width / 2 + bubbleRadius
        ) {
            if (
                pt.y < selectedProps.startY - bubblePadding &&
                pt.y >
                    selectedProps.startY -
                        bubblePadding -
                        canv.actionManager.getZoomConstant(this.topBubbleMove) -
                        2 * bubbleRadius
            ) {
                this.fastActionSide = "top";
                return true;
            }
        }

        canv.redraw();
        this.fastActionSide = "";
        return false;
    }

    reset() {
        this.fastActionArrow = null;
        this.fastActionGroup = null;
    }

    bubbleRadius = 4;
    bubblePadding = 10;
    topBubbleMove = 50;

    fastActionSide: ArrowPlace | "" = "";
    fastActionArrow: Group | null = null;
    fastActionGroup: Group | null = null;
}

export const fastActionManager = new FastActionManager();
